import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchRoomById, updateRoom } from '../reducers/RoomSlice';
import { toast } from 'react-toastify';

function UpdateRooms() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const room = useSelector((state) => state.rooms.room);

  const [formData, setFormData] = useState({
    roomNumber: '',
    roomType: '',
    floorNumber: '',
    capacity: '',
    availableBeds: '',
    facilities: [],
    monthlyRent: '',
    additionalCharges: '',
    roomStatus: '',
    availableFrom: '',
    notes: '',
  });

  useEffect(() => {
    if (id) {
      dispatch(fetchRoomById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (room) {
      setFormData({
        roomNumber: room.roomNumber || '',
        roomType: room.roomType || '',
        floorNumber: room.floorNumber || '',
        capacity: room.capacity || '',
        availableBeds: room.availableBeds || '',
        facilities: room.facilities || [],
        monthlyRent: room.monthlyRent || '',
        additionalCharges: room.additionalCharges || '',
        roomStatus: room.roomStatus || '',
        availableFrom: room.availableFrom || '',
        notes: room.notes || '',
      });
    }
  }, [room]);

  const handleChange = (e) => {
    const { id, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setFormData((prevState) => ({
        ...prevState,
        facilities: checked
          ? [...prevState.facilities, id]
          : prevState.facilities.filter((facility) => facility !== id),
      }));
    } else {
      setFormData((prevState) => ({ ...prevState, [id]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (id) {
      try {
        await dispatch(updateRoom({ id, roomData: formData })).unwrap();
        navigate('/rooms');
        toast.success('Room updated successfully!');
      } catch (error) {
        toast.error('Failed to update the room. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="container mt-1 text-white py-1">
      <h2 className="text-center mb-4">Update Room</h2>
      <form onSubmit={handleSubmit}>
        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <label htmlFor="roomNumber" className="form-label">Room Number</label>
            <input
              type="text"
              className="form-control"
              id="roomNumber"
              value={formData.roomNumber}
              onChange={handleChange}
              placeholder="Enter room number"
              readOnly
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="roomType" className="form-label">Room Type</label>
            <select
              className="form-select"
              id="roomType"
              value={formData.roomType}
              onChange={handleChange}
            >
              <option value="" disabled>Select room type</option>
              <option value="single">Single Occupancy</option>
              <option value="double">Double Occupancy</option>
              <option value="triple">Triple Occupancy</option>
              <option value="fourth">Fourth Occupancy</option>
              <option value="fifth">Fifth Occupancy</option>
            </select>
          </div>

          <div className="col-md-4">
            <label htmlFor="floorNumber" className="form-label">Floor Number</label>
            <select
              className="form-select"
              id="floorNumber"
              value={formData.floorNumber}
              onChange={handleChange}
            >
              <option value="" disabled>Select floor</option>
              <option value="ground">Ground Floor</option>
              <option value="first">First Floor</option>
              <option value="second">Second Floor</option>
            </select>
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-4">
            <label htmlFor="capacity" className="form-label">Maximum Capacity</label>
            <input
              type="number"
              className="form-control"
              id="capacity"
              value={formData.capacity}
              onChange={handleChange}
              placeholder="Enter maximum capacity"
            />
          </div>

          <div className="col-md-4">
            <label htmlFor="availableBeds" className="form-label">Available Beds</label>
            <input
              type="number"
              className="form-control"
              id="availableBeds"
              value={formData.availableBeds}
              onChange={handleChange}
              placeholder="Enter available beds"
            />
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-12">
            <label className="form-label">Room Facilities</label>
            {['AC', 'Bathroom', 'Wifi', 'Study Table', 'Wardrobe', 'Geezer', 'Toilet'].map((facility) => (
              <div className="form-check" key={facility}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={facility}
                  checked={formData.facilities.includes(facility)}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor={facility}>
                  {facility}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label htmlFor="monthlyRent" className="form-label">Monthly Rent</label>
            <input
              type="number"
              className="form-control"
              id="monthlyRent"
              value={formData.monthlyRent}
              onChange={handleChange}
              placeholder="Enter monthly rent"
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="availableFrom" className="form-label">Available From</label>
            <input
              type="date"
              className="form-control"
              id="availableFrom"
              value={formData.availableFrom ? new Date(formData.availableFrom).toISOString().split('T')[0] : ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="row g-3 mb-3">
          {/* <div className="col-md-6">
            <label htmlFor="roomStatus" className="form-label">Room Status</label>
            <select
              className="form-select"
              id="roomStatus"
              value={formData.roomStatus}
              onChange={handleChange}
            >
              <option value="" disabled>Select status</option>
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="maintenance">Under Maintenance</option>
            </select>
          </div> */}
        </div>

        <div className="row g-3 mb-3">
          <div className="col-md-12">
            <label htmlFor="notes" className="form-label">Notes/Description</label>
            <textarea
              className="form-control"
              id="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Enter any additional details"
            ></textarea>
          </div>
        </div>

        <div className="row g-3">
          <div className="col-md-12 d-flex justify-content-end gap-3">
            <button
              type="submit"
              className="btn btn-primary text-white"
              disabled={loading}
            >
              {loading ? (
                <span>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Updating...
                </span>
              ) : (
                'Update'
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default UpdateRooms;
